// NotificationIcon.js (only showing the popup part and relevant structure)

import React, { useEffect, useState, useRef } from 'react';
import { FaFacebookMessenger } from 'react-icons/fa';
import {
  IconButton,
  Badge,
  Avatar,
  Typography,
  Box,
  Fade,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import api from '../../api/api';
import ChatNotificationModal from './ChatNotificationModal';

const NotificationIcon = () => {
  const [notificationschat, setNotificationschat] = useState([]);
  const [unreadCountchat, setUnreadCountchat] = useState(0);
  const [modalOpenchat, setModalOpenchat] = useState(false);
  const [latestPopup, setLatestPopup] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const isRefreshingRef = useRef(false);
  const lastSeenId = useRef(null);
  const audioRef = useRef(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const fetchNotifications = async () => {
    if (isRefreshingRef.current) return;

    try {
      const response = await api.get('/api/users/chat-notifications/');
      const data = response.data.sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      );

      const unread = data.filter((n) => !n.is_read);

      setNotificationschat(data);
      setUnreadCountchat(unread.length);

      // Show popup on new unread notification (ONLY if popup not currently visible)
      if (
        data.length > 0 &&
        !data[0].is_read &&
        data[0].id !== lastSeenId.current
      ) {
        lastSeenId.current = data[0].id;
        setLatestPopup(data[0]);
        setShowPopup(true);

        // Play notification sound
        if (audioRef.current) {
          audioRef.current.play().catch((e) => console.error('Audio play failed:', e));
        }

        // Hide popup after 4 seconds
        setTimeout(() => setShowPopup(false), 4000);
      }
    } catch (err) {
      if (err.response?.status === 401) {
        isRefreshingRef.current = true;
        const interval = setInterval(() => {
          const token = localStorage.getItem('access');
          if (token) {
            isRefreshingRef.current = false;
            clearInterval(interval);
            fetchNotifications();
          } else {
            clearInterval(interval);
          }
        }, 1000);
      } else {
        console.error('Failed to fetch notifications:', err);
      }
    }
  };

  const markAsRead = async (id) => {
    try {
      await api.patch(`/api/users/chat-notifications/${id}/mark_as_read/`);
      const updated = notificationschat.map((n) =>
        n.id === id ? { ...n, is_read: true } : n
      );
      setNotificationschat(updated);
      setUnreadCountchat(updated.filter((n) => !n.is_read).length);
    } catch (err) {
      console.error('Error marking notification as read:', err);
    }
  };

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 10000);
    return () => clearInterval(interval);
  }, []);

  return (
    <Box sx={{ position: 'relative' }}>
      <IconButton
        onClick={() => setModalOpenchat((prev) => !prev)}
        color="inherit"
        aria-label="View chat notifications"
      >
        {unreadCountchat > 0 ? (
          <Badge badgeContent={unreadCountchat} color="error">
            <FaFacebookMessenger title="Messages" size={24} />
          </Badge>
        ) : (
          <FaFacebookMessenger title="Messages" size={24} />
        )}
      </IconButton>

      {modalOpenchat && (
        <ChatNotificationModal
          notificationschat={notificationschat}
          setNotifications={setNotificationschat}
          setUnreadCount={setUnreadCountchat}
          onClose={() => setModalOpenchat(false)}
          markAsRead={markAsRead}
        />
      )}

      {/* Popup alert always rendered and controlled only by showPopup */}
      {latestPopup && (
        <Fade in={showPopup}>
          <Box
            sx={{
              position: 'fixed',
              top: isMobile ? '40%' : 100,
              left: isMobile ? '50%' : 'auto',
              right: isMobile ? 'auto' : 30,
              transform: isMobile ? 'translate(-50%, -50%)' : 'none',
              zIndex: 1300,
              bgcolor: 'background.paper',
              boxShadow: 3,
              borderRadius: 2,
              p: 2,
              minWidth: 250,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              cursor: 'pointer',
            }}
            onClick={() => {
              setModalOpenchat(true);
              setShowPopup(false);
            }}
          >
            <Avatar src={latestPopup.sender_profile_image_url} />
   <Box>
  <Typography fontWeight="bold" noWrap color="#003366">
    {latestPopup.sender_name}
  </Typography>
  <Typography variant="body2" noWrap color="#003366">
    {latestPopup.message_preview.length > 50
      ? latestPopup.message_preview.slice(0, 50) + '...'
      : latestPopup.message_preview}
  </Typography>
</Box>
          </Box>
        </Fade>
      )}

      <audio ref={audioRef} src="/notification.mp3" preload="auto" />
    </Box>
  );
};

export default NotificationIcon;
